import React, { useEffect, useMemo, useState } from 'react';
import { Plug, AlertCircle, Loader2, Search, Info, Unlink, ChevronLeft, ChevronRight } from 'lucide-react';
import {
  listProjectMcpStatus,
  setProjectMcpState,
  revokeProjectMcpToken,
  ProjectMcpStatus,
  McpAccessState
} from '../../services/adminMcpStatus';

const STATE_OPTIONS: { value: McpAccessState; label: string }[] = [
  { value: 'off', label: 'Off' },
  { value: 'readonly', label: 'Read-only' },
  { value: 'write', label: 'Write' },
];

const STATE_FILTER_OPTIONS: { value: McpAccessState | ''; label: string }[] = [
  { value: '', label: 'Any access' },
  ...STATE_OPTIONS,
];

const PAGE_SIZES = [10, 25, 50];

function currentState(p: ProjectMcpStatus): McpAccessState {
  if (!p.mcpEnabled) return 'off';
  return p.mcpWriteEnabled ? 'write' : 'readonly';
}

export default function McpStatusAdmin() {
  const [projects, setProjects] = useState<ProjectMcpStatus[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [orgFilter, setOrgFilter] = useState('');
  const [stateFilter, setStateFilter] = useState<McpAccessState | ''>('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(PAGE_SIZES[1]);
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [rowErrors, setRowErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    listProjectMcpStatus()
      .then(result => setProjects(result.projects))
      .catch(err => setError(err.response?.data?.error || err.message || 'Failed to load MCP status'))
      .finally(() => setLoading(false));
  }, []);

  const organisations = useMemo(
    () => Array.from(new Set(projects.map(p => p.organizationId))).sort(),
    [projects]
  );

  const filtered = useMemo(() => {
    return projects.filter(p => {
      if (orgFilter && p.organizationId !== orgFilter) return false;
      if (stateFilter && currentState(p) !== stateFilter) return false;
      if (searchTerm) {
        const term = searchTerm.toLowerCase();
        if (!p.name.toLowerCase().includes(term) && !p.organizationId.toLowerCase().includes(term)) return false;
      }
      return true;
    });
  }, [projects, searchTerm, orgFilter, stateFilter]);

  // Reset to page 1 whenever the filtered set changes shape, so we never land on a page past the end
  useEffect(() => { setPage(1); }, [searchTerm, orgFilter, stateFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const safePage = Math.min(page, totalPages);
  const paginated = filtered.slice((safePage - 1) * pageSize, safePage * pageSize);

  const enabledCount = projects.filter(p => p.mcpEnabled).length;

  function clearRowError(projectId: string) {
    setRowErrors(prev => {
      if (!(projectId in prev)) return prev;
      const next = { ...prev };
      delete next[projectId];
      return next;
    });
  }

  async function handleSetState(p: ProjectMcpStatus, next: McpAccessState) {
    const prevState = currentState(p);
    if (next === prevState) return;

    if (prevState === 'off' && next !== 'off') {
      const confirmed = window.confirm(
        `Turn MCP ${next === 'write' ? 'on with write access' : 'on (read-only)'} for "${p.name}"?\n\n` +
        `This reuses the token already stored for this project — it is not reissued. ` +
        `If that token may be compromised, revoke it instead and ask the owner to generate a new one.`
      );
      if (!confirmed) return;
    }

    setActioningId(p.projectId);
    clearRowError(p.projectId);
    try {
      await setProjectMcpState(p.projectId, next);
      setProjects(prev => prev.map(row => row.projectId === p.projectId
        ? { ...row, mcpEnabled: next !== 'off', mcpWriteEnabled: next === 'write' }
        : row));
    } catch (err: any) {
      setRowErrors(prev => ({ ...prev, [p.projectId]: err.response?.data?.error || err.message || 'Failed to update' }));
    } finally {
      setActioningId(null);
    }
  }

  async function handleRevoke(p: ProjectMcpStatus) {
    const confirmed = window.confirm(
      `Switch MCP off for "${p.name}" and revoke all ${p.activeTokenCount} member token(s)? Revocation cannot be undone. ` +
      `MCP must be re-enabled, and each member must create a new personal token.`
    );
    if (!confirmed) return;

    setActioningId(p.projectId);
    clearRowError(p.projectId);
    try {
      await revokeProjectMcpToken(p.projectId);
      setProjects(prev => prev.map(row => row.projectId === p.projectId
        ? { ...row, mcpEnabled: false, mcpWriteEnabled: false, activeTokenCount: 0 }
        : row));
    } catch (err: any) {
      setRowErrors(prev => ({ ...prev, [p.projectId]: err.response?.data?.error || err.message || 'Failed to revoke token' }));
      // The server switches MCP off before revoking, so a partial failure still leaves it off
      if (typeof err.response?.data?.revoked === 'number') {
        setProjects(prev => prev.map(row => row.projectId === p.projectId
          ? { ...row, mcpEnabled: false, mcpWriteEnabled: false, activeTokenCount: Math.max(0, row.activeTokenCount - err.response.data.revoked) }
          : row));
      }
    } finally {
      setActioningId(null);
    }
  }

  return (
    <div className="flex-1 overflow-y-auto p-8 font-display">
      <div className="w-full max-w-[1400px] mx-auto flex flex-col gap-6">

        <header className="flex items-center justify-between border-b border-slate-200 pb-6">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary">
              <Plug size={22} />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-navy">MCP Status</h1>
              <p className="text-slate-500 text-sm font-medium">
                Project MCP enablement across every organisation — {enabledCount} of {projects.length} enabled.
              </p>
            </div>
          </div>
        </header>

        <div className="rounded-xl bg-slate-50 border border-slate-200 px-4 py-3 flex gap-3 items-start">
          <Info size={16} className="text-slate-400 flex-shrink-0 mt-0.5" />
          <p className="text-xs text-slate-500">
            Token contents are never shown here — only a hint. Access changes never mint or reveal a token: turning access back on
            from Off reuses whatever token is already stored. To fully kill a token (e.g. it may be compromised), use Revoke — the
            owner must then generate a new one themselves.
          </p>
        </div>

        {error && (
          <div className="px-5 py-4 rounded-xl border border-red-200 bg-red-50 flex gap-3">
            <AlertCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-sm font-medium text-red-700">{error}</p>
          </div>
        )}

        <div className="bg-white border border-[#e2e8f0] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#e2e8f0]">
            <div className="flex flex-wrap gap-3 items-center">
              <div className="relative flex-1 min-w-[200px]">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type="text"
                  placeholder="Search by project or organisation..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-[#e2e8f0] rounded-xl text-sm text-navy placeholder:text-slate-400 focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                />
              </div>
              <select
                value={orgFilter}
                onChange={e => setOrgFilter(e.target.value)}
                className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
              >
                <option value="">All organisations</option>
                {organisations.map(org => (
                  <option key={org} value={org}>{org}</option>
                ))}
              </select>
              <select
                value={stateFilter}
                onChange={e => setStateFilter(e.target.value as McpAccessState | '')}
                className="border border-[#e2e8f0] rounded-xl px-3 py-2 text-sm text-navy focus:ring-2 focus:ring-primary focus:border-transparent outline-none bg-white"
              >
                {STATE_FILTER_OPTIONS.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <span className="ml-auto text-xs text-slate-400">{filtered.length} project{filtered.length !== 1 ? 's' : ''}</span>
            </div>
          </div>

          {loading ? (
            <div className="flex justify-center items-center py-20 text-slate-400">
              <Loader2 size={24} className="animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-16 text-slate-400">
              <Plug size={32} className="mx-auto mb-3 opacity-40" />
              <p className="text-sm font-medium">No projects found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#e2e8f0] bg-slate-50/50">
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Project</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Organisation</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Access</th>
                    <th className="text-left px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Active tokens</th>
                    <th className="text-right px-5 py-3 text-xs font-bold uppercase tracking-wider text-slate-400">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#e2e8f0]">
                  {paginated.map(p => {
                    const state = currentState(p);
                    const busy = actioningId === p.projectId;
                    return (
                      <tr key={p.projectId} className="hover:bg-slate-50/60 transition-colors align-top">
                        <td className="px-5 py-3 font-medium text-navy">{p.name}</td>
                        <td className="px-5 py-3 text-slate-500">{p.organizationId}</td>
                        <td className="px-5 py-3">
                          <div className="flex rounded-xl border border-[#e2e8f0] overflow-hidden w-fit">
                            {STATE_OPTIONS.map(opt => (
                              <button
                                key={opt.value}
                                type="button"
                                disabled={busy}
                                onClick={() => handleSetState(p, opt.value)}
                                className={`px-3 py-1.5 text-xs font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                                  state === opt.value ? 'bg-primary text-white' : 'bg-white text-slate-500 hover:bg-slate-50'
                                }`}
                              >
                                {opt.label}
                              </button>
                            ))}
                          </div>
                          {rowErrors[p.projectId] && (
                            <p className="text-xs text-red-600 mt-1.5 flex items-center gap-1">
                              <AlertCircle size={11} /> {rowErrors[p.projectId]}
                            </p>
                          )}
                        </td>
                        <td className="px-5 py-3 text-slate-400 text-xs">
                          {p.activeTokenCount || '—'}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {busy && <Loader2 size={14} className="animate-spin text-slate-400" />}
                            <button
                              onClick={() => handleRevoke(p)}
                              disabled={busy || !p.activeTokenCount}
                              title="Switch MCP off and revoke all member tokens"
                              className="p-1.5 rounded-lg bg-red-50 text-red-500 hover:bg-red-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-red-50"
                            >
                              <Unlink size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!loading && filtered.length > 0 && (
            <div className="flex items-center justify-between px-5 py-4 border-t border-[#e2e8f0] bg-slate-50/50">
              <div className="flex items-center gap-2 text-xs text-slate-500">
                Rows per page:
                {PAGE_SIZES.map(s => (
                  <button
                    key={s}
                    onClick={() => { setPageSize(s); setPage(1); }}
                    className={`px-2 py-1 rounded-lg transition-colors ${pageSize === s ? 'bg-primary/10 text-primary font-semibold' : 'hover:bg-slate-100'}`}
                  >
                    {s}
                  </button>
                ))}
              </div>

              <div className="flex items-center gap-1">
                <span className="text-xs text-slate-400 mr-2">
                  {(safePage - 1) * pageSize + 1}–{Math.min(safePage * pageSize, filtered.length)} of {filtered.length}
                </span>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={safePage === 1}
                  className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft className="w-4 h-4 text-slate-600" />
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1)
                  .filter(p => p === 1 || p === totalPages || Math.abs(p - safePage) <= 1)
                  .reduce<(number | '…')[]>((acc, p, i, arr) => {
                    if (i > 0 && p - arr[i - 1] > 1) acc.push('…');
                    acc.push(p);
                    return acc;
                  }, [])
                  .map((p, i) => p === '…'
                    ? <span key={`ellipsis-at-${i}`} className="px-1 text-xs text-slate-400">…</span>
                    : (
                    <button
                      key={p}
                      onClick={() => setPage(p)}
                      className={`w-7 h-7 rounded-lg text-xs font-medium transition-colors ${safePage === p ? 'bg-primary text-white' : 'hover:bg-slate-100 text-slate-600'}`}
                    >
                      {p}
                    </button>
                  ))
                }
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={safePage === totalPages}
                  className="p-1.5 rounded-lg hover:bg-slate-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight className="w-4 h-4 text-slate-600" />
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
